/**
 * The branded moment.
 *
 * Mastery's mark is two interlocking diamonds. While the browser does its
 * proof-of-work, they draw themselves and lock together — the logo assembling
 * itself, driven by real computation rather than a fake timer.
 *
 * That's the honest version of the "wow factor": the impressive thing and the
 * security thing are the same thing. Nothing is asked of the visitor.
 */

import {
  MARK_ASPECT,
  MARK_TAILS,
  MARK_VIEWBOX,
  RING_LEFT,
  RING_PERIMETER,
  RING_RIGHT,
  RING_STROKE,
} from "../brandMark";

export function Verifying({ progress }: { progress: number }) {
  // Two rings draw in sequence: the first over the opening half, the second
  // over the closing half, so the mark completes exactly as the work does.
  const first = Math.min(1, progress / 0.5);
  const second = Math.max(0, Math.min(1, (progress - 0.5) / 0.5));
  const locked = progress >= 1;

  // Stroking each ring's centre-line reproduces the ring exactly, and is what
  // makes it drawable with stroke-dashoffset. The interlace tails aren't part
  // of either outline, so they arrive on their own at the end — which is
  // literally the two diamonds locking together.
  const height = 86 / MARK_ASPECT;

  return (
    <div className="verifying" role="status" aria-live="polite">
      <svg width="86" height={height} viewBox={MARK_VIEWBOX} aria-hidden="true">
        {[RING_LEFT, RING_RIGHT].map((d, i) => (
          <path
            key={d}
            d={d}
            fill="none"
            stroke="currentColor"
            strokeWidth={RING_STROKE}
            strokeLinejoin="miter"
            strokeDasharray={RING_PERIMETER}
            strokeDashoffset={RING_PERIMETER * (1 - (i === 0 ? first : second))}
            style={{ transition: "stroke-dashoffset .18s linear" }}
          />
        ))}
        <path
          d={MARK_TAILS}
          fill="currentColor"
          opacity={locked ? 1 : 0}
          style={{ transition: "opacity .22s ease-out" }}
        />
      </svg>

      <p className="verifying-label">
        {locked ? "Ready" : "Preparing your assessment"}
      </p>

      {/* The visitor is told what's happening, in plain language. It's a good
          story rather than something to hide. */}
      <p className="verifying-note">
        Your browser is completing a short security check so this assessment
        stays yours and Mastery Partners&rsquo;. No clicking required.
      </p>

      <span className="sr">
        {locked ? "Verification complete." : "Verifying your browser, please wait."}
      </span>
    </div>
  );
}
